import gql from 'graphql-tag'

// S3 multipart upload. Used by the upload manager for large files
// (src/utils/multipart-upload.js).
export const CREATE_MULTIPART_UPLOAD = gql`
  mutation createMultipartUpload {
    createMultipartUpload {
      storeId
      uploadId
    }
  }
`

export const SIGN_UPLOAD_PARTS = gql`
  mutation signUploadParts($input: SignUploadPartsInput!) {
    signUploadParts(input: $input) {
      urls {
        partNumber
        url
      }
    }
  }
`

export const COMPLETE_MULTIPART_UPLOAD = gql`
  mutation completeMultipartUpload($input: CompleteMultipartUploadInput!) {
    completeMultipartUpload(input: $input) {
      ok
    }
  }
`

export const ABORT_MULTIPART_UPLOAD = gql`
  mutation abortMultipartUpload($input: AbortMultipartUploadInput!) {
    abortMultipartUpload(input: $input) {
      ok
    }
  }
`

export const ADD_LOCATION_MUTATION = gql`
  mutation addLocation($input: AddLocationInput!) {
    addLocation(input: $input) {
      location {
        id
        name
        streetAddress
        secondaryAddress
        city
        state
        zipCode
        parentLocation {
          id
        }
        status
      }
    }
  }
`

export const EDIT_LOCATION_MUTATION = gql`
  mutation changeLocation($input: ChangeLocationInput!) {
    changeLocation(input: $input) {
      location {
        id
        name
        streetAddress
        secondaryAddress
        city
        state
        zipCode
        parentLocation {
          id
        }
        status
      }
    }
  }
`

export const SEND_CERTIFICATE_EMAIL_MUTATION = gql`
  mutation sendCertificateEmail($input: SendCertificateEmailInput!) {
    sendCertificateEmail(input: $input) {
      recipientCount
    }
  }
`

// Certificate audit history (backend migration 00083). Recorded when a batch is
// generated; the zip's SHA-256 ties every later download, email and filing to
// it. Emails are recorded by sendCertificateEmail itself, given the batchId.
export const RECORD_CERTIFICATE_BATCH_MUTATION = gql`
  mutation recordCertificateBatch($input: RecordCertificateBatchInput!) {
    recordCertificateBatch(input: $input) {
      batch {
        id
      }
    }
  }
`

export const RECORD_CERTIFICATE_BATCH_EVENT_MUTATION = gql`
  mutation recordCertificateBatchEvent(
    $input: RecordCertificateBatchEventInput!
  ) {
    recordCertificateBatchEvent(input: $input) {
      event {
        id
        kind
      }
    }
  }
`

// Turn automatic certificates on or off for one exam family.
export const SET_TRAINING_AUTO_CERTIFICATE_MUTATION = gql`
  mutation setTrainingAutoCertificate(
    $input: SetTrainingAutoCertificateInput!
  ) {
    setTrainingAutoCertificate(input: $input) {
      family
      enabled
      updatedByName
      updatedAt
    }
  }
`

export const ADD_USER_MUTATION = gql`
  mutation addUser($input: AddUserInput!) {
    addUser(input: $input) {
      user {
        id
        email
        firstName
        lastName
        # The admin users table pushes this object STRAIGHT into its row list,
        # which reads all three. Without them a brand-new account renders as
        # Disabled with a blank status and no Resend-invitation button, and a
        # fresh invite in FORCE_CHANGE_PASSWORD is the only state Cognito
        # accepts a resend for.
        status
        enabled
        roles
        accountLocations {
          id
          name
        }
      }
    }
  }
`

export const ADD_ROLE_MUTATION = gql`
  mutation addRoleToUser($input: AddRoleToUserInput!) {
    addRoleToUser(input: $input) {
      user {
        id
        email
        firstName
        lastName
        roles
      }
    }
  }
`

export const REMOVE_ROLE_MUTATION = gql`
  mutation removeRoleFromUser($input: RemoveRoleFromUserInput!) {
    removeRoleFromUser(input: $input) {
      user {
        id
        email
        firstName
        lastName
        roles
      }
    }
  }
`

export const ADD_SURVEY_MUTATION = gql`
  mutation addInfraredSurvey($input: AddInfraredSurveyInput!) {
    addInfraredSurvey(input: $input) {
      infraredsurvey {
        id
        date
        status
        description
        operator {
          id
          email
        }
        device {
          id
          name
        }
      }
    }
  }
`

export const CHANGE_INFRARED_SURVEY_MUTATION = gql`
  mutation changeInfraredSurvey($input: ChangeInfraredSurveyInput!) {
    changeInfraredSurvey(input: $input) {
      infraredsurvey {
        id
        date
        status
        operator {
          id
          email
        }
        device {
          id
          name
        }
      }
    }
  }
`

export const REMOVE_INFRARED_SURVEY_MUTATION = gql`
  mutation removeInfraredSurvey($input: RemoveInfraredSurveyInput!) {
    removeInfraredSurvey(input: $input) {
      id
    }
  }
`

export const REMOVE_INFRARED_SURVEY_PHOTO_MUTATION = gql`
  mutation removeInfraredSurveyPhoto($input: RemoveInfraredSurveyPhotoInput!) {
    removeInfraredSurveyPhoto(input: $input) {
      id
    }
  }
`

export const ADD_IR_FAULT_OBSERVATION_MUTATION = gql`
  mutation addIRFaultObservation($input: AddIRFaultObservationInput!) {
    addIRFaultObservation(input: $input) {
      fault {
        id
        type
        fixed
        recommendations
        notes
        referenceTemperature
        spotTemperature
        measuredCurrentPhaseA
        measuredCurrentPhaseB
        measuredCurrentPhaseC
        millivoltDropPhaseA
        millivoltDropPhaseB
        millivoltDropPhaseC
        # photos {
        #   id
        #   name
        #   link
        #   size
        #   camera
        # }
      }
    }
  }
`

export const CHANGE_IR_FAULT_OBSERVATION_MUTATION = gql`
  mutation changeIRFaultObservation($input: ChangeIRFaultObservationInput!) {
    changeIRFaultObservation(input: $input) {
      fault {
        id
        type
        fixed
        recommendations
        notes
        referenceTemperature
        spotTemperature
        measuredCurrentPhaseA
        measuredCurrentPhaseB
        measuredCurrentPhaseC
        millivoltDropPhaseA
        millivoltDropPhaseB
        millivoltDropPhaseC
        # photos {
        #   id
        #   name
        #   link
        #   size
        #   camera
        # }
      }
    }
  }
`

export const REMOVE_IR_FAULT_MUTATION = gql`
  mutation removeIRFault($input: RemoveIRFaultInput!) {
    removeIRFault(input: $input) {
      id
    }
  }
`

export const REMOVE_INFRARED_FAULT_PHOTO_MUTATION = gql`
  mutation removeInfraredFaultPhoto($input: RemoveInfraredFaultPhotoInput!) {
    removeInfraredFaultPhoto(input: $input) {
      id
    }
  }
`

export const ADD_IR_FAULT_CODE_VIOLATION_MUTATION = gql`
  mutation addIRFaultCodeViolation($input: AddIRFaultCodeViolationInput!) {
    addIRFaultCodeViolation(input: $input) {
      fault {
        id
        type
        fixed
        notes
        shortDescription
        description
        notes
        recommendations
      }
    }
  }
`

export const CHANGE_IR_FAULT_CODE_VIOLATION_MUTATION = gql`
  mutation changeIRFaultCodeViolation(
    $input: ChangeIRFaultCodeViolationInput!
  ) {
    changeIRFaultCodeViolation(input: $input) {
      fault {
        id
        type
        fixed
        notes
        shortDescription
        description
        notes
        recommendations
      }
    }
  }
`

export const ADD_INFRARED_SURVEY_PHOTO_MUTATION = gql`
  mutation addInfraredSurveyPhoto($input: AddInfraredSurveyPhotoInput!) {
    addInfraredSurveyPhoto(input: $input) {
      photo {
        id
        name
        surveyPhotoLink
        surveyId
      }
    }
  }
`

export const ADD_INFRARED_FAULT_PHOTO_MUTATION = gql`
  mutation addInfraredFaultPhoto($input: AddInfraredFaultPhotoInput!) {
    addInfraredFaultPhoto(input: $input) {
      photo {
        id
        name
        faultPhotoLink
        faultId
      }
    }
  }
`

export const ADD_INFRARED_SURVEY_PHOTOS_MUTATION = gql`
  mutation addInfraredSurveyPhotos($input: AddInfraredSurveyPhotosInput!) {
    addInfraredSurveyPhotos(input: $input) {
      photos {
        id
        name
        surveyPhotoLink
        surveyId
      }
    }
  }
`

export const ADD_INFRARED_FAULT_PHOTOS_MUTATION = gql`
  mutation addInfraredFaultPhotos($input: AddInfraredFaultPhotosInput!) {
    addInfraredFaultPhotos(input: $input) {
      photos {
        id
        name
        faultPhotoLink
        faultId
      }
    }
  }
`
export const ADD_ACCOUNT_MUTATION = gql`
  mutation addAccount($input: AddAccountInput!) {
    addAccount(input: $input) {
      user {
        id
        email
        firstName
        lastName
        accountLocations {
          id
          name
        }
      }
    }
  }
`

// Grants every enabled location in one request. Deliberately sends no location ids: the
// server enumerates them. The previous approach collected them in the browser by
// paginating the location list 15 rows at a time, ~109 chained requests, which is what
// made "Add All" appear to hang.
//
// `accountLocations` is NOT selected back. For a user who now holds every location that
// would be thousands of rows nobody reads, and the page refetches its own query anyway.
// The count is what the UI needs to report what happened.
export const ADD_ALL_ACCOUNTS_MUTATION = gql`
  mutation addAllAccounts($input: AddAllAccountsInput!) {
    addAllAccounts(input: $input) {
      locationsGranted
      user {
        id
        email
      }
    }
  }
`

export const DISABLE_USER_MUTATION = gql`
  mutation disableUser($input: DisableUserInput!) {
    disableUser(input: $input) {
      user {
        id
        email
        firstName
        lastName
        enabled
        status
      }
    }
  }
`
export const ENABLE_USER_MUTATION = gql`
  mutation enableUser($input: EnableUserInput!) {
    enableUser(input: $input) {
      user {
        id
        email
        firstName
        lastName
        enabled
        status
      }
    }
  }
`
export const ENABLE_LOCATION_MUTATION = gql`
  mutation changeLocation($input: ChangeLocationInput!) {
    changeLocation(input: $input) {
      # How many DESCENDANTS the cascade changed, so the toast can report the
      # real number rather than the subtree size the dialog showed.
      cascadedCount
      location {
        id
        name
        streetAddress
        secondaryAddress
        city
        state
        zipCode
        latitude
        longitude
        status
      }
    }
  }
`

export const DISABLE_LOCATION_MUTATION = gql`
  mutation changeLocation($input: ChangeLocationInput!) {
    changeLocation(input: $input) {
      # How many DESCENDANTS the cascade archived, so the toast can report the
      # real number rather than the subtree size the dialog showed.
      cascadedCount
      location {
        id
        name
        streetAddress
        secondaryAddress
        city
        state
        zipCode
        latitude
        longitude
        status
      }
    }
  }
`

export const RESEND_EMAIL_MUTATION = gql`
  mutation resendEmail($input: ResendEmailInput!) {
    resendEmail(input: $input) {
      user {
        id
      }
    }
  }
`

export const RESET_PASSWORD_MUTATION = gql`
  mutation resetPassword($input: ResetPasswordInput!) {
    resetPassword(input: $input) {
      user {
        id
      }
    }
  }
`

export const REMOVE_ACCOUNT_MUTATION = gql`
  mutation removeAccount($input: RemoveAccountInput!) {
    removeAccount(input: $input) {
      user {
        id
        email
        firstName
        lastName
        accountLocations {
          id
          name
        }
      }
    }
  }
`
export const REMOVE_USER_MUTATION = gql`
  mutation removeUser($input: RemoveUserInput!) {
    removeUser(input: $input) {
      id
    }
  }
`

export const ADD_DEVICE_MUTATION = gql`
  mutation addDevice($input: AddDeviceInput!) {
    addDevice(input: $input) {
      device {
        id
        name
        tag
        settings {
          voltage
          voltageType
          manufacturer
          floor
          room
          serialNumber
          phase
          kva
          amperage
          type
        }
        location {
          id
          name
        }
        gloveClass
      }
    }
  }
`

export const CHANGE_DEVICE_MUTATION = gql`
  mutation changeDevice($input: ChangeDeviceInput!) {
    changeDevice(input: $input) {
      device {
        id
        name
        tag
        settings {
          voltage
          voltageType
          manufacturer
          floor
          room
          serialNumber
          phase
          kva
          amperage
          type
        }
        location {
          id
          name
        }
        gloveClass
      }
    }
  }
`

export const ENABLE_DEVICE_MUTATION = gql`
  mutation enableDevice($input: EnableDeviceInput!) {
    enableDevice(input: $input) {
      id
    }
  }
`

export const DISABLE_DEVICE_MUTATION = gql`
  mutation disableDevice($input: DisableDeviceInput!) {
    disableDevice(input: $input) {
      id
    }
  }
`

// REMOVE_DEVICE_MUTATION went with the server field. removeDevice is retired in
// favour of DISABLE_DEVICE_MUTATION above, which takes a device out of the
// infrared page and the reports without destroying its surveys, faults, photos or
// documents. Sending the old document would now be a validation error.

// Notify people with access to a location that something was uploaded for it.
// Recipients are USER IDS, not addresses: the server re-checks each one against
// the location's access list, so a stale picker (or a crafted request) cannot
// mail someone who could not open the upload anyway.
export const SEND_LOCATION_UPLOAD_EMAIL_MUTATION = gql`
  mutation sendLocationUploadEmail($input: SendLocationUploadEmailInput!) {
    sendLocationUploadEmail(input: $input) {
      recipientCount
    }
  }
`

export const REMOVE_DOCUMENT_MUTATION = gql`
  mutation removeDocument($input: AdminRemoveDocumentInput!) {
    adminRemoveDocument(input: $input) {
      id
    }
  }
`

// Move a location's Documents or Training file to its Archived tab, or back.
// Admins only (the server refuses anyone else, and project files outright).
export const ARCHIVE_DOCUMENT_MUTATION = gql`
  mutation archiveDocument($input: ArchiveDocumentInput!) {
    archiveDocument(input: $input) {
      document {
        id
        archivedAt
        archivedByName
      }
    }
  }
`

export const RESTORE_DOCUMENT_MUTATION = gql`
  mutation restoreDocument($input: RestoreDocumentInput!) {
    restoreDocument(input: $input) {
      document {
        id
        archivedAt
        archivedByName
      }
    }
  }
`

export const IMPORT_ARC_FLASH_MUTATION = gql`
  mutation importArcFlashData($input: ImportArcFlashDataInput!) {
    importArcFlashData(input: $input) {
      processing
    }
  }
`

export const IMPORT_DOCUMENT_MUTATION = gql`
  mutation importDocumentData($input: AddDocumentInput!) {
    addDocument(input: $input) {
      document {
        id
        name
        link
      }
    }
  }
`

export const IMPORT_TRAINING_DOCUMENT_MUTATION = gql`
  mutation addTrainingDocument($input: AddTrainingDocumentInput!) {
    addTrainingDocument(input: $input) {
      document {
        id
        name
        link
      }
    }
  }
`

export const IMPORT_PROJECT_DOCUMENT_MUTATION = gql`
  mutation addProjectDocument($input: AddProjectDocumentInput!) {
    addProjectDocument(input: $input) {
      document {
        id
        name
        link
      }
    }
  }
`

export const IMPORT_LOTO_DOCUMENT_MUTATION = gql`
  mutation addLotoDocument($input: AddLOTODocumentInput!) {
    addLotoDocument(input: $input) {
      document {
        id
        name
        link
      }
    }
  }
`

export const CLOSE_SUPPORT_TICKET = gql`
  mutation closeSupportTicket($input: CloseSupportTicket!) {
    closeSupportTicket(input: $input) {
      supportTicket {
        id
        name
        email
        company
        status
        updatedAt
      }
    }
  }
`
export const OPEN_SUPPORT_TICKET = gql`
  mutation openSupportTicket($input: OpenSupportTicket!) {
    openSupportTicket(input: $input) {
      supportTicket {
        id
        name
        email
        company
        status
        updatedAt
      }
    }
  }
`

export const ADD_SUPPORT_TICKET = gql`
  mutation addSupportTicket($input: AddSupportTicket!) {
    addSupportTicket(input: $input) {
      supportTicket {
        name
        company
        email
        status
        createdAt
        updatedAt
      }
    }
  }
`

export const ADD_DEVICE_CONDITION_OF_MAINTENANCE = gql`
  mutation addDeviceConditionOfMaintenance(
    $input: AddDeviceConditionOfMaintenanceInput!
  ) {
    addDeviceConditionOfMaintenance(input: $input) {
      conditionOfMaintenance {
        conditionOfMaintenanceID
        shortDescription
        description
        deviceID
      }
    }
  }
`

export const UPDATE_DEVICE_CONDITION_OF_MAINTENANCE = gql`
  mutation updateDeviceConditionOfMaintenance(
    $input: UpdateDeviceConditionOfMaintenanceInput!
  ) {
    updateDeviceConditionOfMaintenance(input: $input) {
      conditionOfMaintenance {
        conditionOfMaintenanceID
        shortDescription
        description
        deviceID
      }
    }
  }
`
export const RESET_DEVICE_CONDITION_OF_MAINTENANCE = gql`
  mutation resetDeviceConditionOfMaintenance(
    $input: ResetDeviceConditionOfMaintenanceInput!
  ) {
    resetDeviceConditionOfMaintenance(input: $input) {
      id
    }
  }
`
export const GENERATE_INFRARED_REPORT = gql`
  mutation generateInfraredReport($input: GenerateInfraredReportInput!) {
    generateInfraredReport(input: $input) {
      locationId
      presignUrl
    }
  }
`
// Legacy Infrared archive report generation. The deletable bridge that replaces
// the retired auto_generate_reports tool. See src/router/views/legacy-infrared.vue.
export const GENERATE_LEGACY_INFRARED_REPORT = gql`
  mutation generateLegacyInfraredReport(
    $input: GenerateLegacyInfraredReportInput!
  ) {
    generateLegacyInfraredReport(input: $input) {
      downloadUrl
    }
  }
`
export const ADD_LOCATION_LOGO_MUTATION = gql`
  mutation addLocationLogo($input: AddLocationLogoInput!) {
    addLocationLogo(input: $input) {
      logo {
        id
        name
        logoLink
        locationId
      }
    }
  }
`

// Records a completed public training-quiz attempt. Server-side counterpart is
// net-new (no quiz/submission types existed in schema.graphql as of this
// feature). The backend must implement `submitQuizAttempt(input:
// SubmitQuizAttemptInput!)` with:
//   input {
//     quizSlug: String!        # which quiz (matches src/data/training-quizzes.js)
//     quizTitle: String        # denormalized for reporting convenience
//     name: String!            # taker identity (no login on these pages)
//     email: String!
//     company: String          # company / facility the taker is associated with
//     score: Int!              # integer percentage 0–100
//     passed: Boolean!
//     totalQuestions: Int!
//     correctCount: Int!
//     answers: [QuizAnswerInput!]!   # { questionId: String!, choiceId: String! }
//   }
// The client computes score/passed (content is hardcoded in the frontend), so
// the server should treat those as reported values, not trust them for any
// high-stakes gating.
export const SUBMIT_QUIZ_ATTEMPT_MUTATION = gql`
  mutation submitQuizAttempt($input: SubmitQuizAttemptInput!) {
    submitQuizAttempt(input: $input) {
      attempt {
        id
        quizSlug
        score
        passed
        submittedAt
      }
    }
  }
`

// Delete one recorded attempt from an admin results table. Gated to
// ADMIN/DEVELOPER on the server (matches the quizAttempts query).
export const DELETE_QUIZ_ATTEMPT_MUTATION = gql`
  mutation deleteQuizAttempt($input: DeleteQuizAttemptInput!) {
    deleteQuizAttempt(input: $input) {
      id
    }
  }
`

export const SET_SCHEDULE_ASSIGNMENT_MUTATION = gql`
  mutation setScheduleAssignment($input: SetScheduleAssignmentInput!) {
    setScheduleAssignment(input: $input) {
      id
      userId
      date
      kind
      status
      workTypes
      locationId
      locationName
      locationPlace
      notes
    }
  }
`

export const REMOVE_SCHEDULE_ASSIGNMENT_MUTATION = gql`
  mutation removeScheduleAssignment($input: RemoveScheduleAssignmentInput!) {
    removeScheduleAssignment(input: $input)
  }
`

// Overwrite an occupied day and push the displaced event(s) forward. `placements`
// is the resolved chain: [0] is the newly-scheduled event; each later entry is a
// displaced event moved to a new future date (movedFromDate set). Applied
// atomically by the backend.
export const RESCHEDULE_SCHEDULE_ASSIGNMENTS_MUTATION = gql`
  mutation rescheduleScheduleAssignments(
    $input: RescheduleScheduleAssignmentsInput!
  ) {
    rescheduleScheduleAssignments(input: $input) {
      id
      userId
      date
      kind
      status
      workTypes
      locationId
      locationName
      locationPlace
      notes
      jobGroupId
    }
  }
`

// Record a PDF attachment for a job (by its group id) and get back the row plus
// a presigned PUT URL the browser uploads the file to.
export const ADD_SCHEDULE_ATTACHMENT_MUTATION = gql`
  mutation addScheduleAttachment($input: AddScheduleAttachmentInput!) {
    addScheduleAttachment(input: $input) {
      attachment {
        id
        name
        downloadUrl
      }
      uploadUrl
    }
  }
`

export const REMOVE_SCHEDULE_ATTACHMENT_MUTATION = gql`
  mutation removeScheduleAttachment($id: ID!) {
    removeScheduleAttachment(id: $id)
  }
`

export const CLEAR_SCHEDULE_ASSIGNMENTS_MUTATION = gql`
  mutation clearScheduleAssignments($input: ClearScheduleAssignmentsInput!) {
    clearScheduleAssignments(input: $input)
  }
`

// Mark a single notification read (returns true if it changed).
export const MARK_NOTIFICATION_READ_MUTATION = gql`
  mutation markNotificationRead($id: ID!) {
    markNotificationRead(id: $id)
  }
`

// Mark all of the caller's notifications read (returns how many changed).
export const MARK_ALL_NOTIFICATIONS_READ_MUTATION = gql`
  mutation markAllNotificationsRead {
    markAllNotificationsRead
  }
`

// --- LOTO procedures -------------------------------------------------------
// Creates the procedure for this device + energy type, or returns the existing
// one. Idempotent, so the editor can open a type without knowing whether it has
// been authored before.
export const UPSERT_LOTO_PROCEDURE_MUTATION = gql`
  mutation upsertLotoProcedure($input: UpsertLotoProcedureInput!) {
    upsertLotoProcedure(input: $input) {
      procedure {
        id
        deviceId
        type
        steps {
          id
          stepNumber
          description
          action
          info
          verification
          photos {
            id
            stepId
            name
            size
            link
          }
        }
      }
    }
  }
`

// Every draft procedure of one piece of equipment marked final at once: the
// procedure added in one go is reviewed in one go.
export const FINALIZE_LOTO_DEVICE_PROCEDURES_MUTATION = gql`
  mutation finalizeLotoDeviceProcedures($deviceID: ID!) {
    finalizeLotoDeviceProcedures(deviceID: $deviceID) {
      id
      type
      status
      finalizedAt
      finalizedBy
    }
  }
`

// One control point edited from the wizard's screens. Answers with its whole
// procedure, which the card merges in.
export const CHANGE_LOTO_CONTROL_POINT_MUTATION = gql`
  mutation changeLotoControlPoint($input: ChangeLotoControlPointInput!) {
    changeLotoControlPoint(input: $input) {
      # The control point's own id and its photo's: what a change queued
      # offline learns its real ids from (loto-offline-mutations.js).
      step {
        id
        photos {
          id
        }
      }
      procedure {
        id
        deviceId
        type
        status
        finalizedAt
        finalizedBy
        details
        otherHazards {
          type
          notes
          labels
        }
        updatedAt
        steps {
          id
          procedureId
          stepNumber
          description
          action
          info
          verification
          controlPoint
          controlPointName
          lockoutEquipment
          actionFields {
            key
            value
          }
          photos {
            id
            stepId
            name
            size
            link
            shapes {
              type
              color
              x1
              y1
              x2
              y2
            }
          }
        }
      }
    }
  }
`

// One more control point at the end of a procedure, from the wizard's control
// point and lockout photo screens. Answers with its whole procedure.
export const ADD_LOTO_CONTROL_POINT_MUTATION = gql`
  mutation addLotoControlPoint($input: AddLotoControlPointInput!) {
    addLotoControlPoint(input: $input) {
      # The control point's own id and its photo's: what a change queued
      # offline learns its real ids from (loto-offline-mutations.js).
      step {
        id
        photos {
          id
        }
      }
      procedure {
        id
        deviceId
        type
        status
        finalizedAt
        finalizedBy
        details
        otherHazards {
          type
          notes
          labels
        }
        updatedAt
        steps {
          id
          procedureId
          stepNumber
          description
          action
          info
          verification
          controlPoint
          controlPointName
          lockoutEquipment
          actionFields {
            key
            value
          }
          photos {
            id
            stepId
            name
            size
            link
            shapes {
              type
              color
              x1
              y1
              x2
              y2
            }
          }
        }
      }
    }
  }
`

// A procedure from the Add Procedure wizard, replacing the equipment's existing
// ones (`replaces` names them, as the person was warned). Answers with the
// equipment and every procedure it has now.
export const SAVE_LOTO_EQUIPMENT_PROCEDURE_MUTATION = gql`
  mutation saveLotoEquipmentProcedure(
    $input: SaveLotoEquipmentProcedureInput!
  ) {
    saveLotoEquipmentProcedure(input: $input) {
      device {
        id
        equipmentLocation
        photo {
          name
          link
        }
      }
      procedures {
        id
        deviceId
        type
        status
        finalizedAt
        finalizedBy
        details
        otherHazards {
          type
          notes
          labels
        }
        steps {
          id
          procedureId
          stepNumber
          description
          action
          info
          verification
          controlPoint
          controlPointName
          lockoutEquipment
          actionFields {
            key
            value
          }
          photos {
            id
            stepId
            name
            size
            link
            shapes {
              type
              color
              x1
              y1
              x2
              y2
            }
          }
        }
      }
    }
  }
`

// Removes the procedure and, with it, its steps and their photos.
export const REMOVE_LOTO_PROCEDURE_MUTATION = gql`
  mutation removeLotoProcedure($input: RemoveLotoProcedureInput!) {
    removeLotoProcedure(input: $input) {
      id
    }
  }
`

// Every procedure of one piece of equipment, steps and photos with them: the
// editor's "Delete All Procedures". One server transaction, so all or nothing.
// Answers with the ids removed, which the editor evicts from the cache.
export const REMOVE_LOTO_PROCEDURES_MUTATION = gql`
  mutation removeLotoProcedures($input: RemoveLotoProceduresInput!) {
    removeLotoProcedures(input: $input) {
      ids
    }
  }
`

// Appends a step. Returns the whole procedure too, so the caller gets the
// resulting numbering without a refetch.
export const ADD_LOTO_PROCEDURE_STEP_MUTATION = gql`
  mutation addLotoProcedureStep($input: AddLotoProcedureStepInput!) {
    addLotoProcedureStep(input: $input) {
      step {
        id
        stepNumber
        description
        action
        info
        verification
      }
      procedure {
        id
        deviceId
        type
        steps {
          id
          stepNumber
          description
          action
          info
          verification
          photos {
            id
            stepId
            name
            size
            link
          }
        }
      }
    }
  }
`

// Only the fields sent are written, which is what lets the four be edited
// independently. Omitting one leaves it as it was.
export const CHANGE_LOTO_PROCEDURE_STEP_MUTATION = gql`
  mutation changeLotoProcedureStep($input: ChangeLotoProcedureStepInput!) {
    changeLotoProcedureStep(input: $input) {
      step {
        id
        stepNumber
        description
        action
        info
        verification
        photos {
          id
          stepId
          name
          size
          link
        }
      }
    }
  }
`

// Returns the procedure with the remaining steps renumbered to close the gap.
export const REMOVE_LOTO_PROCEDURE_STEP_MUTATION = gql`
  mutation removeLotoProcedureStep($input: RemoveLotoProcedureStepInput!) {
    removeLotoProcedureStep(input: $input) {
      id
      procedure {
        id
        deviceId
        type
        steps {
          id
          stepNumber
          description
          action
          info
          verification
          photos {
            id
            stepId
            name
            size
            link
          }
        }
      }
    }
  }
`

// stepIDs must list every step of the procedure in the wanted order. The API
// rejects a partial list rather than guessing.
export const REORDER_LOTO_PROCEDURE_STEPS_MUTATION = gql`
  mutation reorderLotoProcedureSteps($input: ReorderLotoProcedureStepsInput!) {
    reorderLotoProcedureSteps(input: $input) {
      procedure {
        id
        deviceId
        type
        steps {
          id
          stepNumber
          description
          action
          info
          verification
          photos {
            id
            stepId
            name
            size
            link
          }
        }
      }
    }
  }
`

// Adds photos to the END of a step's photo list (a step holds up to six, the
// most that always fit one printed page). Each storeId is the cache id from
// uploadLinkDocument, same as the infrared photo mutations. A replay of the same
// upload is a no-op server-side, and `photos` hands back the ones asked for, in
// order: the offline outbox reads the new photo's id from photos.0.id.
export const ADD_LOTO_PROCEDURE_STEP_PHOTOS_MUTATION = gql`
  mutation addLotoProcedureStepPhotos(
    $input: AddLotoProcedureStepPhotosInput!
  ) {
    addLotoProcedureStepPhotos(input: $input) {
      step {
        id
        photos {
          id
          stepId
          name
          size
          link
        }
      }
      photos {
        id
        stepId
        name
        size
        link
      }
    }
  }
`

// Removes ONE photo by its own id. Removing one that is already gone succeeds,
// with a null step.
export const REMOVE_LOTO_STEP_PHOTO_MUTATION = gql`
  mutation removeLotoStepPhoto($input: RemoveLotoStepPhotoInput!) {
    removeLotoStepPhoto(input: $input) {
      id
      step {
        id
        photos {
          id
          stepId
          name
          size
          link
        }
      }
    }
  }
`

// Pastes copied procedures onto a piece of equipment (copy/paste in the editor).
// Text only, one transaction; a type the target already has is replaced. The
// payload is every procedure on the target afterwards.
export const COPY_LOTO_PROCEDURES_MUTATION = gql`
  mutation copyLotoProcedures($input: CopyLotoProceduresInput!) {
    copyLotoProcedures(input: $input) {
      procedures {
        id
        deviceId
        type
        updatedAt
        steps {
          id
          procedureId
          stepNumber
          description
          action
          info
          verification
          photos {
            id
            stepId
            name
            size
            link
          }
        }
      }
    }
  }
`

// DEPRECATED single-photo form: removes the step's FIRST photo. Still used for
// a photo in an offline download made before steps held several, which carries
// no photo id of its own; that photo is the only one such a step had.
export const REMOVE_LOTO_PROCEDURE_STEP_PHOTO_MUTATION = gql`
  mutation removeLotoProcedureStepPhoto(
    $input: RemoveLotoProcedureStepPhotoInput!
  ) {
    removeLotoProcedureStepPhoto(input: $input) {
      step {
        id
        photos {
          id
          stepId
          name
          size
          link
        }
      }
    }
  }
`

// Renders the printable lockout procedure sheet as a PDF and returns a link to
// it. Send exactly ONE of procedureID (a single energy type), deviceID (every
// energy type on that device, on one sheet) or locationID (every device at the
// site that has a procedure, each on its own sheet, in one file). The server
// rejects any other combination rather than guessing which document was wanted.
export const GENERATE_LOTO_REPORT_MUTATION = gql`
  mutation generateLotoReport($input: GenerateLotoReportInput!) {
    generateLotoReport(input: $input) {
      presignUrl
    }
  }
`

// --- Technicians (location-scoped; see the backend's schema/technician.gql) ---
// The fixed Bowtie Engineering default refuses all three mutations server-side.
export const ADD_TECHNICIAN_MUTATION = gql`
  mutation addTechnician($input: AddTechnicianInput!) {
    addTechnician(input: $input) {
      technician {
        id
        name
        title
        phoneNumber
        level
        voltageMax
        incidentEnergy
        isDefault
      }
    }
  }
`

export const CHANGE_TECHNICIAN_MUTATION = gql`
  mutation changeTechnician($input: ChangeTechnicianInput!) {
    changeTechnician(input: $input) {
      technician {
        id
        name
        title
        phoneNumber
        level
        voltageMax
        incidentEnergy
        isDefault
      }
    }
  }
`

// Unassigns from the location; the backend deletes the row once it is assigned
// nowhere.
export const REMOVE_TECHNICIAN_MUTATION = gql`
  mutation removeTechnician($input: RemoveTechnicianInput!) {
    removeTechnician(input: $input) {
      id
    }
  }
`

export const SET_LOCATION_ARC_FLASH_STUDY_DATE_MUTATION = gql`
  mutation setLocationArcFlashStudyDate(
    $input: SetLocationArcFlashStudyDateInput!
  ) {
    setLocationArcFlashStudyDate(input: $input) {
      location {
        id
        lastArcFlashStudy
      }
    }
  }
`

// The infrared twin. That date is normally stamped when a report is generated;
// this is the admin correction from the location page.
export const SET_LOCATION_INFRARED_STUDY_DATE_MUTATION = gql`
  mutation setLocationInfraredStudyDate(
    $input: SetLocationInfraredStudyDateInput!
  ) {
    setLocationInfraredStudyDate(input: $input) {
      location {
        id
        lastInfraredStudy
      }
    }
  }
`

// Records the outreach against EVERY study the location currently needs. What
// the combined page's single checkbox claims. Studies already current are left
// untouched, so ticking cannot fabricate a contact about one nobody discussed.
export const SET_LOCATION_RENEWALS_CONTACTED_MUTATION = gql`
  mutation setLocationRenewalsContacted(
    $input: SetLocationRenewalsContactedInput!
  ) {
    setLocationRenewalsContacted(input: $input) {
      renewal {
        location {
          id
        }
        studiesNeeded
        arcFlashDaysOverdue
        infraredDaysOverdue
        arcFlashDaysUntilDue
        infraredDaysUntilDue
        contacted
        contactedAt
        contactedBy
        rootLocationId
        rootLocationName
      }
    }
  }
`

// LOTO equipment (backend migration 00063). Only the name is required on add;
// on change only the fields SENT are written, so an omitted one keeps its value
// while an explicitly empty one clears it.
export const ADD_LOTO_DEVICE_MUTATION = gql`
  mutation addLotoDevice($input: AddLotoDeviceInput!) {
    addLotoDevice(input: $input) {
      device {
        id
        locationId
        name
        equipmentLocation
        equipmentId
      }
    }
  }
`

export const CHANGE_LOTO_DEVICE_MUTATION = gql`
  mutation changeLotoDevice($input: ChangeLotoDeviceInput!) {
    changeLotoDevice(input: $input) {
      device {
        id
        locationId
        name
        equipmentLocation
        equipmentId
      }
    }
  }
`

// Removing a device removes every procedure written against it, and their steps.
export const REMOVE_LOTO_DEVICE_MUTATION = gql`
  mutation removeLotoDevice($input: RemoveLotoDeviceInput!) {
    removeLotoDevice(input: $input) {
      id
    }
  }
`

// Bulk import from the LOTO device template. The client parses the spreadsheet
// and sends rows, so the operator sees exactly what will be written first.
export const IMPORT_LOTO_DEVICES_MUTATION = gql`
  mutation importLotoDevices($input: ImportLotoDevicesInput!) {
    importLotoDevices(input: $input) {
      imported
      skipped
    }
  }
`

// A user editing their OWN profile. Deliberately separate from CHANGE_USER,
// which is admin-only and can rename or re-address any account: this one takes
// no user id at all. The server reads it from the session.
export const UPDATE_MY_PROFILE_MUTATION = gql`
  mutation updateMyProfile($input: UpdateMyProfileInput!) {
    updateMyProfile(input: $input) {
      user {
        id
        title
      }
    }
  }
`

// Admin edit of another user. Company lives HERE and nowhere else. A user
// cannot set their own employer from their profile. Title is editable both
// here and by the person themselves (UPDATE_MY_PROFILE_MUTATION).
export const CHANGE_USER_MUTATION = gql`
  mutation changeUser($input: ChangeUserInput!) {
    changeUser(input: $input) {
      user {
        id
        email
        company
        title
      }
    }
  }
`

// Signing a lockout as applied. `checkedOrders` is every apply-phase step's
// displayOrder: the server rebuilds the checklist and refuses the signature
// unless all of them are present, so this is not advisory.
export const APPLY_LOTO_LOCKOUT_MUTATION = gql`
  mutation applyLotoLockout($input: ApplyLotoLockoutInput!) {
    applyLotoLockout(input: $input) {
      event {
        id
        status
        appliedSignatureName
        appliedAt
        equipmentState
        otherPersonnel
      }
    }
  }
`

// Releasing it. Requires every RESTORE step, and a reason when the person
// signing is not the one who applied it.
export const RELEASE_LOTO_LOCKOUT_MUTATION = gql`
  mutation releaseLotoLockout($input: ReleaseLotoLockoutInput!) {
    releaseLotoLockout(input: $input) {
      event {
        id
        status
        releasedSignatureName
        releasedAt
        releaseOverrideReason
      }
    }
  }
`

// Starts a lockout and creates its board: one hold per hazard the machine has.
// NOTHING IS LOCKED OUT BY THIS. It is the empty board people then sign onto,
// which is why it takes no signature.
export const OPEN_LOTO_LOCKOUT_MUTATION = gql`
  mutation openLotoLockout($input: OpenLotoLockoutInput!) {
    openLotoLockout(input: $input) {
      group {
        id
        mode
        requiredEnergyTypes
        fullyCovered
        uncoveredEnergyTypes
        holds {
          id
          energyType
          # The control point this hold is (2026-10-23); blank label and no
          # step on a hold that stands for a whole hazard.
          stepId
          pointLabel
          pointNumber
          covered
          plannedUser {
            id
            firstName
            lastName
          }
        }
      }
    }
  }
`

// Throws away an open lockout NOBODY IS LOCKED ONTO.
//
// This is the only way out of a board that was opened and never signed: nothing
// else closes a lockout except its last release, and a lockout with no
// signatures has no release. Without it one abandoned board blocks that machine
// for ever.
//
// Refused while anybody is locked on, by a WHERE clause rather than a check, so
// somebody signing on mid-cancel cannot have the machine freed underneath them.
export const CANCEL_LOTO_LOCKOUT_MUTATION = gql`
  mutation cancelLotoLockout($input: CancelLotoLockoutInput!) {
    cancelLotoLockout(input: $input) {
      group {
        id
        closedAt
      }
    }
  }
`

// Assigns a hazard to one person, or clears it with a null userId.
//
// An assignment is a PERMISSION: once set, only that person can sign for the
// hazard and everybody else's Take button goes dead. Only the coordinator who
// opened the lockout, or an admin, may call this, and the server re-checks it.
//
// It is still not PROTECTION: the hazard stays uncovered until its holder
// actually signs. Refused once somebody is locked onto it.
export const PLAN_LOTO_LOCKOUT_HOLD_MUTATION = gql`
  mutation planLotoLockoutHold($input: PlanLotoLockoutHoldInput!) {
    planLotoLockoutHold(input: $input) {
      group {
        id
        fullyCovered
        uncoveredEnergyTypes
        holds {
          id
          energyType
          # The control point this hold is (2026-10-23); blank label and no
          # step on a hold that stands for a whole hazard.
          stepId
          pointLabel
          pointNumber
          covered
          plannedUser {
            id
            firstName
            lastName
          }
        }
      }
    }
  }
`

// The equipment's other hazards, dangers, warnings or notes, set on one of its
// procedures on their own, replacing the list it had (an empty one clears it).
// Answers with that procedure.
export const SET_LOTO_PROCEDURE_OTHER_HAZARDS_MUTATION = gql`
  mutation setLotoProcedureOtherHazards(
    $input: SetLotoProcedureOtherHazardsInput!
  ) {
    setLotoProcedureOtherHazards(input: $input) {
      procedure {
        id
        deviceId
        type
        status
        finalizedAt
        finalizedBy
        details
        otherHazards {
          type
          notes
          labels
        }
        updatedAt
      }
    }
  }
`

// Archives a piece of LOTO equipment instead of deleting it: it leaves the list,
// the readiness counts and the site binder, and keeps its procedures and lockout
// records. Refused while it is locked out.
export const ARCHIVE_LOTO_DEVICE_MUTATION = gql`
  mutation archiveLotoDevice($deviceID: ID!) {
    archiveLotoDevice(deviceID: $deviceID) {
      id
      archivedAt
    }
  }
`

// Puts archived LOTO equipment back in use, as it was.
export const RESTORE_LOTO_DEVICE_MUTATION = gql`
  mutation restoreLotoDevice($deviceID: ID!) {
    restoreLotoDevice(deviceID: $deviceID) {
      id
      archivedAt
    }
  }
`

// Saves the roles changed on the Role Permissions page, together. Each carries
// the updatedAt it was read at, so a change somebody else made meanwhile comes
// back as a CONFLICT rather than being overwritten. Returns the whole grid.
export const SET_ROLE_PERMISSIONS_MUTATION = gql`
  mutation setRolePermissions($input: SetRolePermissionsInput!) {
    setRolePermissions(input: $input) {
      role
      siteScope
      permissions
      userCount
      editable
      builtin
      updatedAt
      updatedBy
    }
  }
`

// Adds a role to the grid with the sites it reaches and its permissions, from
// the Role Permissions page. Returns the whole grid.
export const CREATE_ROLE_MUTATION = gql`
  mutation addRole($input: AddRoleInput!) {
    addRole(input: $input) {
      role
      siteScope
      permissions
      userCount
      editable
      builtin
      updatedAt
      updatedBy
    }
  }
`

// Deletes a role added from the Role Permissions page. Refused for a built-in
// role and while anybody holds it. Returns the whole grid.
export const DELETE_ROLE_MUTATION = gql`
  mutation removeRole($name: String!) {
    removeRole(name: $name) {
      role
      siteScope
      permissions
      userCount
      editable
      builtin
      updatedAt
      updatedBy
    }
  }
`
